/**
 * Parallel LMS Staff Management System — Tab 1 "Loan Profile & Customer
 * Details" (Phase 10A frontend, plan §5.2 / Task 1).
 *
 * Master-detail layout:
 *   left  — searchable + paginated active-loan table (react-data-table-component,
 *           same DataTable/customStyles pattern as pagesadmin/RoleManagement.tsx)
 *   right — the selected loan's profile: a top-left selfie avatar that opens a
 *           selfie+Aadhaar modal, identity fields, plain-text registered phone
 *           with Call + WhatsApp buttons, "Last 7 Days Payments", "This Month's
 *           Amort Schedule" (with a jump to the full schedule),
 *           verification-media gallery with per-item download links, a read-only
 *           map pin, and the "Download All Verification Media" zip link.
 *
 * This tab is entirely READ-ONLY (Task 1 defines no mutating action), so it
 * takes no `canEdit` prop and performs no permission gating of its own beyond
 * the tab-level `can_view` check already done by LmsStaffOpsHub.
 *
 * FOLLOW-UP ADDITION — the three new sections consume fields that
 * `LoanProfileView` now returns in the SAME single request
 * (`selfie_media` / `aadhaar_media` / `last_7_days_payments` /
 * `this_month_amort`), so no extra round-trip and no new endpoint was needed.
 * "View Full Amort Schedule" reuses the existing cross-tab `LmsStaffNav` jump
 * into Tab 2, which already renders the complete schedule from
 * LoanAmortMandateView — the full table is deliberately NOT duplicated here.
 */
import React from "react";
import DataTable from "react-data-table-component";
import { useQuery } from "@tanstack/react-query";
import {
    Search,
    RefreshCcw,
    Phone,
    MessageCircle,
    Download,
    MapPin,
    FileText,
    User,
    Wallet,
    CalendarClock,
    ExternalLink,
    ImageOff,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import lmsStaffService, {
    ActiveLoanRow,
    LoanProfile,
    LoanProfilePaymentRow,
    VerificationMediaItem,
    getLmsApiErrorMessage,
} from "./lmsStaffApi";

import {
    formatAmount,
    formatDate,
    formatDateTime,
    formatDateTimeIST,
    digitsOnly,
    dataTableStyles as customStyles,
} from "./formatters";
import { useLmsStaffNav } from "./lmsStaffNav";
import LoanSummaryDialog from "./LoanSummaryDialog";
import LoanExtDialog from "./LoanExtDialog";
import { TWO_LINE_BUTTON_CLASS, TwoLineLabel } from "./twoLineButton";

// ---------------------------------------------------------------------------
// Read-only map pin
// ---------------------------------------------------------------------------

/**
 * Read-only "show a pin" map.
 *
 * `components/MapAddressPicker.tsx` was evaluated for reuse and is NOT usable
 * here: it is a write-only component (a Dialog whose sole output is an
 * `onAddressSelect` callback fired when the operator picks a place — it has no
 * controlled lat/lng input and no display-only mode). Rather than refactor a
 * component used in the live customer journey, this uses the plain Google Maps
 * embed, which needs no API key and cannot be interacted with destructively.
 */
const ReadOnlyMapPin: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => (
    <div className="space-y-2">
        <iframe
            title="Customer location"
            src={`https://www.google.com/maps?q=${lat},${lng}&output=embed`}
            className="h-64 w-full rounded-lg border border-gray-200"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
                {lat.toFixed(6)}, {lng.toFixed(6)}
            </span>
            <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noreferrer"
                className="text-intellend-green underline"
            >
                Open in Google Maps
            </a>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {label}
        </p>
        <div className="text-sm text-gray-900 break-words">{children}</div>
    </div>
);

const MediaCard: React.FC<{ media: VerificationMediaItem }> = ({ media }) => (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                    {media.verification_type || "Unknown type"}
                </p>
                <p className="text-xs text-gray-500">
                    {media.media_type || "-"} · {formatDateTime(media.taken_at)}
                </p>
            </div>
        </div>
        {media.download_url ? (
            <a
                href={media.download_url}
                target="_blank"
                rel="noreferrer"
                download
                className="inline-flex items-center gap-1 text-xs font-medium text-intellend-green underline"
            >
                <Download className="h-3.5 w-3.5" /> Download
            </a>
        ) : (
            <p className="text-xs text-amber-600">
                Download link unavailable (signed URL generation failed
                server-side)
            </p>
        )}
    </div>
);

// ---------------------------------------------------------------------------
// Selfie avatar -> selfie + Aadhaar modal (top-left of the profile)
// ---------------------------------------------------------------------------

/**
 * One image inside the modal. `download_url` is a signed secure-download link
 * (primary_app.apis.secure_download_view, which streams with
 * `Content-Disposition: inline` and `Access-Control-Allow-Origin: *`), so it
 * renders directly in an <img>. A null URL means signed-URL generation failed
 * server-side, and a load error is caught so a broken image never renders as a
 * silent blank box.
 */
const MediaImage: React.FC<{
    label: string;
    media: VerificationMediaItem | null;
}> = ({ label, media }) => {
    const [failed, setFailed] = React.useState(false);

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {label}
                </p>
                {media?.taken_at && (
                    <span className="text-xs text-gray-400">
                        {formatDateTime(media.taken_at)}
                    </span>
                )}
            </div>
            {media?.download_url && !failed ? (
                <img
                    src={media.download_url}
                    alt={label}
                    onError={() => setFailed(true)}
                    className="max-h-[420px] w-full rounded-lg border border-gray-200 object-contain"
                />
            ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-center">
                    <ImageOff className="h-5 w-5 text-gray-400" />
                    <p className="px-4 text-xs text-gray-500">
                        {!media
                            ? `No ${label.toLowerCase()} on file for this customer.`
                            : !media.download_url
                              ? "Secure download link unavailable (signed URL generation failed server-side)."
                              : `Unable to display this ${label.toLowerCase()}.`}
                    </p>
                </div>
            )}
        </div>
    );
};

/**
 * Small round selfie thumbnail rendered at the TOP-LEFT of the profile header.
 * Clicking it opens the shadcn `Dialog` (same component/pattern already used by
 * pagesadmin/AdminDashboard.tsx and RoleManagement.tsx) showing the selfie on
 * top and the Aadhaar photo directly below it, stacked vertically.
 *
 * When no selfie exists the avatar degrades to a neutral placeholder; it stays
 * clickable so staff can still open the modal to confirm what IS on file (the
 * Aadhaar photo may exist even when the selfie does not).
 */
const CustomerPhotoAvatar: React.FC<{
    customerName: string | null;
    selfie: VerificationMediaItem | null;
    aadhaar: VerificationMediaItem | null;
}> = ({ customerName, selfie, aadhaar }) => {
    const [thumbFailed, setThumbFailed] = React.useState(false);
    const showThumb = !!selfie?.download_url && !thumbFailed;

    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    aria-label="View customer selfie and Aadhaar photo"
                    title="View selfie & Aadhaar photo"
                    className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-intellend-green"
                >
                    {showThumb ? (
                        <img
                            src={selfie!.download_url as string}
                            alt={customerName || "Customer selfie"}
                            onError={() => setThumbFailed(true)}
                            className="h-full w-full object-cover transition group-hover:opacity-80"
                        />
                    ) : (
                        <span className="flex h-full w-full items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                        </span>
                    )}
                </button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {customerName || "Customer"} — Verification Photos
                    </DialogTitle>
                    <DialogDescription>
                        Selfie photo above, Aadhaar photo below.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-5">
                    <MediaImage label="Selfie Photo" media={selfie} />
                    <Separator />
                    <MediaImage label="Aadhaar Photo" media={aadhaar} />
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Last 7 days payments
// ---------------------------------------------------------------------------

/**
 * TTransaction.tran_category_code values, per
 * LMS_Loan_Management_api.insert_transaction_record()'s
 * `_COLLECTION_TYPE_MAP` (10/20/30) — labelled so staff don't read raw codes.
 */
const TRAN_CATEGORY_LABELS: Record<string, string> = {
    "10": "Scheduled",
    "20": "Foreclosure",
    "30": "Cancellation",
};

const Last7DaysPayments: React.FC<{ rows: LoanProfilePaymentRow[] }> = ({ rows }) => (
    <div className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Wallet className="h-4 w-4 text-intellend-green" /> Last 7 Days Payments{" "}
            <span className="font-normal text-gray-500">({rows.length})</span>
        </h3>
        {rows.length === 0 ? (
            <p className="text-sm text-gray-500">
                No payments received on this loan in the last 7 days.
            </p>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                        <tr>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Principal</th>
                            <th className="px-3 py-2">Interest</th>
                            <th className="px-3 py-2">Fee</th>
                            <th className="px-3 py-2">Type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.transaction_id}
                                className="border-t border-gray-100"
                            >
                                <td className="px-3 py-2 text-gray-700">
                                    {formatDate(row.date_of_transaction)}
                                </td>
                                <td className="px-3 py-2 font-medium text-gray-900">
                                    {formatAmount(row.tran_amt)}
                                </td>
                                <td className="px-3 py-2 text-gray-700">
                                    {formatAmount(row.amount_principal)}
                                </td>
                                <td className="px-3 py-2 text-gray-700">
                                    {formatAmount(row.amount_interest)}
                                </td>
                                <td className="px-3 py-2 text-gray-700">
                                    {formatAmount(row.amount_fee)}
                                </td>
                                <td className="px-3 py-2">
                                    <Badge variant="outline" className="text-xs">
                                        {TRAN_CATEGORY_LABELS[
                                            String(row.tran_category_code)
                                        ] || String(row.tran_category_code ?? "-")}
                                    </Badge>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

// ---------------------------------------------------------------------------
// This month's amort schedule
// ---------------------------------------------------------------------------

/**
 * The current month's amort rows only. "View Full Amort Schedule" jumps to Tab
 * 2 (Mandate & Payments) with this loan pre-selected — that tab already renders
 * the complete schedule from LoanAmortMandateView, so the full table is reused
 * rather than duplicated. The jump uses the same `useLmsStaffNav().openLoan`
 * context that <LoanIdLink> uses; when the operator lacks `can_view` on
 * `mandate_pg`, `canOpen` is false and the button is omitted rather than
 * rendering a link that would 403.
 */
const ThisMonthAmort: React.FC<{
    profile: LoanProfile;
}> = ({ profile }) => {
    const { openLoan, canOpen } = useLmsStaffNav();
    const { rows, month_start, month_end, total_amort_rows } = profile.this_month_amort;
    const canOpenMandateTab = canOpen("mandate_pg");

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <CalendarClock className="h-4 w-4 text-intellend-green" /> This Month's
                    Amort Schedule{" "}
                    <span className="font-normal text-gray-500">({rows.length})</span>
                </h3>
                {canOpenMandateTab && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => openLoan(profile.intellend_loan_id, "mandate_pg")}
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> View Full Amort Schedule
                        {total_amort_rows ? ` (${total_amort_rows})` : ""}
                    </Button>
                )}
            </div>
            <p className="text-xs text-gray-500">
                {formatDate(month_start)} &ndash; {formatDate(month_end)}
            </p>
            {rows.length === 0 ? (
                <p className="text-sm text-gray-500">
                    No amortisation rows scheduled for this loan this month.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2">Series</th>
                                <th className="px-3 py-2">Payment Date</th>
                                <th className="px-3 py-2">Amount</th>
                                <th className="px-3 py-2">Principal</th>
                                <th className="px-3 py-2">Interest</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr
                                    key={`${row.loan_series ?? "row"}-${row.payment_date ?? index}`}
                                    className="border-t border-gray-100"
                                >
                                    <td className="px-3 py-2 text-gray-700">
                                        {row.loan_series ?? "-"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {formatDate(row.payment_date)}
                                    </td>
                                    <td className="px-3 py-2 font-medium text-gray-900">
                                        {formatAmount(row.payment_amount)}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {formatAmount(row.payment_amount_p)}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {formatAmount(row.payment_amount_i)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const LoanProfileDetail: React.FC<{ loanId: string }> = ({ loanId }) => {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ["lms-staff-loan-profile", loanId],
        queryFn: async () => {
            const response = await lmsStaffService.getLoanProfile(loanId);
            return response.data as LoanProfile;
        },
        enabled: !!loanId,
    });

    if (isLoading) {
        return (
            <div className="py-20 text-center text-sm text-gray-500">
                Loading loan profile...
            </div>
        );
    }

    if (isError || !data) {
        const message = getLmsApiErrorMessage(
            error,
            "Unable to load this loan's profile."
        );
        return (
            <div className="space-y-3">
                <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                    {message}
                </div>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <RefreshCcw className="mr-2 h-4 w-4" /> Retry
                </Button>
            </div>
        );
    }

    const phone = data.registered_phone || "";
    const waPhone = digitsOnly(phone);

    return (
        <div className="space-y-6">
            {/* Header — selfie avatar sits top-left, per the profile spec */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                    <CustomerPhotoAvatar
                        customerName={data.customer_name}
                        selfie={data.selfie_media}
                        aadhaar={data.aadhaar_media}
                    />
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                            <User className="h-5 w-5 text-intellend-green" />
                            {data.customer_name || "Unnamed customer"}
                        </h2>
                        <p className="mt-1 text-xs text-gray-500">
                            {data.intellend_loan_id}
                        </p>
                    </div>
                </div>
                {/* "Download All Verification Media" keeps its original
                    full-width single-line form on top; the two narrower record
                    actions share ONE row beneath it, each with a two-line label
                    (see TwoLineLabel) so both fit side by side instead of
                    stacking and pushing the profile fields down. */}
                <div className="flex flex-col items-stretch gap-2 sm:items-end">
                    <a
                        href={lmsStaffService.getDownloadAllMediaUrl(
                            data.intellend_loan_id
                        )}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button
                            type="button"
                            size="sm"
                            className="flex w-full items-center gap-2 bg-intellend-green hover:bg-intellend-green/90"
                        >
                            <Download className="h-4 w-4" /> Download All
                            Verification Media
                        </Button>
                    </a>
                    <div className="flex flex-wrap items-stretch gap-2 sm:justify-end">
                        <a
                            href={lmsStaffService.getDownloadPayloadsUrl(
                                data.intellend_loan_id
                            )}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className={TWO_LINE_BUTTON_CLASS}
                            >
                                <Download className="h-4 w-4" />
                                <TwoLineLabel top="Download" bottom="Payloads" />
                            </Button>
                        </a>
                        {/* Older cached responses (or any future caller) may not
                            carry loan_summary — render nothing rather than a
                            button that would open an empty dialog. */}
                        {data.loan_summary && (
                            <LoanSummaryDialog
                                summary={data.loan_summary}
                                loanId={data.intellend_loan_id}
                            />
                        )}
                        {/* Same guard as loan_summary above: a response from
                            before this field existed renders without the button
                            rather than opening an empty dialog. */}
                        {data.loan_ext && (
                            <LoanExtDialog
                                ext={data.loan_ext}
                                loanId={data.intellend_loan_id}
                            />
                        )}
                    </div>
                </div>
            </div>

            <Separator />

            {/* Identity + loan fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Customer Name">{data.customer_name || "-"}</Field>
                <Field label="Business Name">{data.business_name || "-"}</Field>
                <Field label="Cust ID">{data.cust_id || "-"}</Field>
                <Field label="Disbursement UTR">
                    {data.disbursement_utr || "-"}
                </Field>
                <Field label="Disbursed Amount">
                    {formatAmount(data.disbursed_amount)}
                </Field>
                <Field label="Disbursal Date">
                    {formatDateTimeIST(data.date_of_disbursal)}
                </Field>
                {/*
                 * Latest RPS (repayment schedule) arrival time. formatDateTime,
                 * not formatDate, because the TIME is the point of this field —
                 * staff use it to tell how fresh the schedule is. Sits directly
                 * under Disbursal Date since the two are read together and are
                 * routinely different (the push often lands a day or more after
                 * the disbursal's business date).
                 */}
                <Field label="Latest RPS Received">
                    {formatDateTime(data.rps_received_at)}
                </Field>
                <Field label="Lender">{data.lender_code || "-"}</Field>
                <Field label="ROI / Tenure">
                    {data.roi ?? "-"}
                    {data.roi !== null && data.roi !== undefined ? "%" : ""} ·{" "}
                    {data.tenure ?? "-"}
                </Field>
            </div>

            <Separator />

            {/* Registered phone — plain text + Call + WhatsApp */}
            <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Contact</h3>
                <Field label="Registered Phone">
                    {/* Plain text, per Task 1 */}
                    <span className="font-mono">{phone || "-"}</span>
                </Field>
                {phone ? (
                    <div className="flex flex-wrap gap-2">
                        <a href={`tel:${phone}`}>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                            >
                                <Phone className="h-4 w-4" /> Call
                            </Button>
                        </a>
                        {/* Mirrors CallbackHelp.tsx: https://wa.me/91<digits> */}
                        <a
                            href={`https://wa.me/91${waPhone}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </Button>
                        </a>
                    </div>
                ) : (
                    <p className="text-xs text-gray-500">
                        No registered phone on file for this loan.
                    </p>
                )}
            </div>

            <Separator />

            {/* Last 7 days payments (below the profile fields) */}
            <Last7DaysPayments rows={data.last_7_days_payments} />

            <Separator />

            {/* This month's amort schedule + jump to the full schedule */}
            <ThisMonthAmort profile={data} />

            <Separator />

            {/* Verification media gallery */}
            <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">
                    Verification Media{" "}
                    <span className="font-normal text-gray-500">
                        ({data.verification_media.length})
                    </span>
                </h3>
                {data.verification_media.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No Aadhaar / selfie verification media found for this
                        customer.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {data.verification_media.map((media) => (
                            <MediaCard key={media.id} media={media} />
                        ))}
                    </div>
                )}
            </div>

            <Separator />

            {/* Location */}
            <div className="space-y-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <MapPin className="h-4 w-4 text-intellend-green" /> Location
                </h3>
                {data.location ? (
                    <ReadOnlyMapPin
                        lat={data.location.lat}
                        lng={data.location.lng}
                    />
                ) : (
                    <p className="text-sm text-gray-500">
                        No geo-coordinates recorded for this customer's
                        communication or residential address.
                    </p>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 400;

export interface LoanProfilePageProps {
    /**
     * can_edit for the `loan_profile` tab, passed down by LmsStaffOpsHub.
     * Accepted for prop-contract consistency with the other tabs; this tab has
     * no mutating action (Task 1 is read-only display) so it is unused.
     */
    canEdit?: boolean;
    /**
     * Loan pre-selected by another tab (the overview/calendar drill-downs
     * hyperlink into this page). Both the search box and the detail pane adopt
     * it so the operator lands directly on that loan's profile.
     */
    initialLoanId?: string | null;
}

const LoanProfilePage: React.FC<LoanProfilePageProps> = ({ initialLoanId }) => {
    const [searchInput, setSearchInput] = React.useState(initialLoanId || "");
    const [search, setSearch] = React.useState(initialLoanId || "");
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(25);
    const [selectedLoanId, setSelectedLoanId] = React.useState<string | null>(
        initialLoanId || null
    );

    // A later deep-link (operator clicks another loan on the overview tab while
    // this tab is already mounted) must re-target this page.
    React.useEffect(() => {
        if (initialLoanId) {
            setSearchInput(initialLoanId);
            setSelectedLoanId(initialLoanId);
        }
    }, [initialLoanId]);

    // Debounced search — the backend does the filtering (server-side paging).
    React.useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ["lms-staff-active-loans", search, page, pageSize],
        queryFn: async () => {
            const response = await lmsStaffService.getActiveLoans({
                search: search || undefined,
                page,
                page_size: pageSize,
            });
            return response.data;
        },
    });

    const rows: ActiveLoanRow[] = data?.results ?? [];

    const columns = React.useMemo(
        () => [
            {
                name: "Loan ID",
                selector: (row: ActiveLoanRow) => row.intellend_loan_id,
                sortable: true,
                width: "170px",
                cell: (row: ActiveLoanRow) => (
                    <span
                        className="font-mono text-xs cursor-pointer hover:text-intellend-green hover:underline"
                        onClick={() => setSelectedLoanId(row.intellend_loan_id)}
                    >
                        {row.intellend_loan_id}
                    </span>
                ),
            },
            {
                name: "Customer",
                selector: (row: ActiveLoanRow) => row.lender_customer_id || "-",
                sortable: true,
            },
            {
                name: "Disbursed Amount",
                selector: (row: ActiveLoanRow) =>
                    row.disbursed_amount === null ? "" : String(row.disbursed_amount),
                sortable: true,
                cell: (row: ActiveLoanRow) => formatAmount(row.disbursed_amount),
                width: "150px",
            },
            {
                name: "Amt in Bank",
                selector: (row: ActiveLoanRow) =>
                    row.offer_loan_amt_net === null ? "" : String(row.offer_loan_amt_net),
                sortable: true,
                cell: (row: ActiveLoanRow) => formatAmount(row.offer_loan_amt_net),
                width: "140px",
            },
            {
                name: "Daily EDI",
                selector: (row: ActiveLoanRow) =>
                    row.offer_daily_repayment === null ? "" : String(row.offer_daily_repayment),
                sortable: true,
                cell: (row: ActiveLoanRow) => formatAmount(row.offer_daily_repayment),
                width: "130px",
            },
            {
                name: "Total Fee",
                selector: (row: ActiveLoanRow) =>
                    row.offer_total_fee === null ? "" : String(row.offer_total_fee),
                sortable: true,
                cell: (row: ActiveLoanRow) => formatAmount(row.offer_total_fee),
                width: "130px",
            },
            {
                name: "Repay Total",
                selector: (row: ActiveLoanRow) =>
                    row.offer_repay_total === null ? "" : String(row.offer_repay_total),
                sortable: true,
                cell: (row: ActiveLoanRow) => formatAmount(row.offer_repay_total),
                width: "140px",
            },
            {
                name: "Disbursal Date",
                selector: (row: ActiveLoanRow) => row.date_of_disbursal || "",
                sortable: true,
                cell: (row: ActiveLoanRow) => formatDateTimeIST(row.date_of_disbursal),
                width: "180px",
            },
            {
                name: "Status",
                selector: (row: ActiveLoanRow) => row.current_status || "-",
                sortable: true,
                width: "170px",
                cell: (row: ActiveLoanRow) => (
                    <Badge variant="outline" className="text-xs">
                        {row.current_status || "UNKNOWN"}
                    </Badge>
                ),
            },
        ],
        []
    );

    return (
        <div className="w-full space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Loan Profile &amp; Customer Details
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Active loans only. Select a row to open the customer
                            profile.
                        </p>
                    </div>
                    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <Input
                                placeholder="Search loan ID, customer ID or name..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => refetch()}
                            className="flex items-center gap-2"
                        >
                            <RefreshCcw className="h-4 w-4" /> Refresh
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    {/* Master — loan table */}
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                        <DataTable
                            columns={columns}
                            data={rows}
                            pagination
                            paginationServer
                            paginationTotalRows={data?.count ?? 0}
                            paginationDefaultPage={page}
                            onChangePage={(newPage: number) => setPage(newPage)}
                            onChangeRowsPerPage={(newPerPage: number, newPage: number) => {
                                setPageSize(newPerPage);
                                setPage(newPage);
                            }}
                            paginationPerPage={pageSize}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            progressPending={isLoading}
                            progressComponent={
                                <div className="py-20 text-center text-sm text-gray-500">
                                    Loading active loans...
                                </div>
                            }
                            noDataComponent={
                                <div className="py-20 text-center text-sm text-gray-500">
                                    No active loans found.
                                </div>
                            }
                            customStyles={customStyles}
                            highlightOnHover
                            pointerOnHover
                            onRowClicked={(row: ActiveLoanRow) =>
                                setSelectedLoanId(row.intellend_loan_id)
                            }
                            selectableRowsHighlight
                            conditionalRowStyles={[
                                {
                                    when: (row: ActiveLoanRow) =>
                                        row.intellend_loan_id === selectedLoanId,
                                    style: { backgroundColor: "#e6f0ee" },
                                },
                            ]}
                        />
                    </div>

                    {/* Detail — selected loan profile */}
                    <div className="rounded-xl border border-gray-200 p-5">
                        {selectedLoanId ? (
                            <LoanProfileDetail loanId={selectedLoanId} />
                        ) : (
                            <div className="py-20 text-center text-sm text-gray-500">
                                Select a loan from the list to view the customer
                                profile.
                            </div>
                        )}
                    </div>
                </div>

                {isError && (
                    <div className="mt-4 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                        Unable to load the active-loan list. Please try again
                        later.
                    </div>
                )}
                {isFetching && !isLoading && (
                    <p className="mt-2 text-xs text-gray-400">Refreshing...</p>
                )}
            </div>
        </div>
    );
};

export default LoanProfilePage;
